// Pure spherical helpers for the solar widget (docs/12-solar-roof-scan.md §5.1).
//
// The solar widget draws roofs, panels and outlines on a Google map, but the
// maths behind them must not call google.maps.geometry: pure code can be run
// (and checked) in Node by scripts/e2e/solar-geometry-check.mjs, and it keeps
// the geometry the same whether or not the Maps API has loaded yet.
//
// Conventions used everywhere in widget/src/solar:
//   - headings are compass degrees: 0 = north, 90 = east, clockwise;
//   - distances are metres;
//   - everything is plan view (as seen from directly above).
//
// The great-circle functions use the same mean Earth radius as
// google.maps.geometry.spherical, so a point offset here lands exactly where the
// map would put it. makeFrame() gives a flat x/y frame (x east, y north, metres)
// that is accurate over one building (tens of metres), for the fitting and
// trimming maths in outline.ts.
//
// No DOM, no window, no google.*: keep it that way.

import type { LL } from "../types";

// ─── Constants ───────────────────────────────────────────────────────────────

/** Mean Earth radius in metres, as used by google.maps.geometry.spherical. */
export const EARTH_RADIUS_M = 6371008.8;

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

// ─── Great-circle helpers ────────────────────────────────────────────────────

/**
 * The point `dist` metres from `p` along the initial compass heading
 * `headingDeg` (great-circle destination). `dist` should be ≥ 0; use offset()
 * for signed distances.
 */
export function computeOffset(p: LL, dist: number, headingDeg: number): LL {
  const d = dist / EARTH_RADIUS_M; // angular distance
  const h = headingDeg * RAD;
  const lat1 = p.lat * RAD;
  const lng1 = p.lng * RAD;
  // Standard spherical destination formula
  const sinLat2 = Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(h);
  const lat2 = Math.asin(Math.max(-1, Math.min(1, sinLat2)));
  const lng2 = lng1 + Math.atan2(Math.sin(h) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * sinLat2);
  // Wrap longitude into −180..180
  let lng = lng2 * DEG;
  lng = ((((lng + 180) % 360) + 360) % 360) - 180;
  return { lat: lat2 * DEG, lng };
}

/** computeOffset that also accepts negative distances (a negative distance goes the opposite way). */
export function offset(p: LL, dist: number, headingDeg: number): LL {
  return dist < 0 ? computeOffset(p, -dist, headingDeg + 180) : computeOffset(p, dist, headingDeg);
}

/** Initial compass bearing from a to b, in −180..180 (as google.maps.geometry.spherical.computeHeading). */
export function computeHeading(a: LL, b: LL): number {
  const lat1 = a.lat * RAD;
  const lat2 = b.lat * RAD;
  const dLng = (b.lng - a.lng) * RAD;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const h = Math.atan2(y, x) * DEG;
  // atan2 already gives −180..180; fold +180 exactly onto −180 for a single representation
  return h === 180 ? -180 : h;
}

/** The point a fraction f of the way from a to b. Linear is accurate enough at building scale. */
export function interpolate(a: LL, b: LL, f: number): LL {
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f };
}

// ─── Flat local frame ────────────────────────────────────────────────────────

/** A flat x/y frame in metres around an origin: x points east, y points north. */
export interface Frame {
  toXY: (p: LL) => [number, number];
  toLL: (xy: [number, number]) => LL;
}

/**
 * A flat local frame centred on `origin`, accurate over one building. Used to
 * do rectangle fitting and projections in plain 2D maths.
 */
export function makeFrame(origin: LL): Frame {
  // Metres per degree of latitude, and of longitude at this latitude
  const mLat = 110574;
  const mLng = 111320 * Math.cos(origin.lat * RAD);
  return {
    toXY: (p: LL): [number, number] => [(p.lng - origin.lng) * mLng, (p.lat - origin.lat) * mLat],
    toLL: ([x, y]: [number, number]): LL => ({ lat: origin.lat + y / mLat, lng: origin.lng + x / mLng }),
  };
}
